import { useEffect, useId, useRef, useState } from 'react';
import { peopleGateway } from '../index';
import type { DepartmentOption } from '../types';
import '../../compensation/compensation.css';

const ADD_NEW = '__add_department__';

interface DepartmentPickerProps {
  /** The selected department id, or '' for none. */
  value: string;
  onChange: (departmentId: string) => void;
  /** Hidden when the caller may not write employees: the server enforces it either way, this only avoids offering a refused action. */
  canAdd?: boolean;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Department dropdown with "+ Add New Department" (Figma admin screen 17). Choosing it opens a small dialog; on success the department is SAVED for the
 * caller's own company by the server (so it appears for every later employee), the list is refreshed, and the new department is selected in place
 * without touching anything else on the page. A name that already exists is not duplicated: the server returns the existing department and it is
 * selected with a note. The server decides permission, uniqueness and audit; a refusal is shown as is. Nothing is held only in component state.
 */
export function DepartmentPicker({ value, onChange, canAdd = true, disabled = false, required = false }: DepartmentPickerProps) {
  const selectId = useId();
  const titleId = useId();
  const selectRef = useRef<HTMLSelectElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void peopleGateway
      .listDepartments()
      .then((d) => {
        if (!cancelled) setDepartments(d.filter((x) => x.isActive));
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the departments.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (open) nameRef.current?.focus();
  }, [open]);

  function close() {
    setOpen(false);
    setName('');
    setError(null);
    selectRef.current?.focus();
  }

  async function add() {
    const trimmed = name.replace(/\s+/g, ' ').trim();
    if (!trimmed) {
      setError('Enter a department name.');
      return;
    }
    if (trimmed.length > 100) {
      setError('The department name must be 100 characters or fewer.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const created = await peopleGateway.createDepartment(trimmed);
      const existed = departments.some((d) => d.id === created.id);
      setDepartments((prev) => (prev.some((d) => d.id === created.id) ? prev : [...prev, created]));
      setNote(existed ? `${created.name} already exists, so it was selected.` : `${created.name} was added and selected.`);
      onChange(created.id);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the department. You may not have permission to add one.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <label htmlFor={selectId}>
        Department {required && <span aria-hidden="true">*</span>}
      </label>
      <select
        id={selectId}
        ref={selectRef}
        value={value}
        disabled={disabled}
        required={required}
        onChange={(e) => {
          setNote(null);
          if (e.target.value === ADD_NEW) {
            setOpen(true);
            return;
          }
          onChange(e.target.value);
        }}
      >
        <option value="">{required ? 'Select a department' : 'Not set'}</option>
        {departments.map((d) => (
          <option key={d.id} value={d.id}>{d.name}</option>
        ))}
        {canAdd && <option value={ADD_NEW}>+ Add New Department</option>}
      </select>
      {loadError && <p className="error" role="alert">{loadError}</p>}
      {note && <p className="muted" role="status">{note}</p>}

      {open && (
        <div className="compensation-modal-backdrop" role="presentation" onClick={() => !busy && close()}>
          <div
            className="compensation-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && !busy) close();
            }}
          >
            <div className="compensation-modal-head">
              <h3 id={titleId}>Add New Department</h3>
              <button type="button" className="compensation-modal-close" aria-label="Close" onClick={close} disabled={busy}>×</button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void add();
              }}
            >
              <div className="compensation-modal-body">
                <label>
                  Department name <span aria-hidden="true">*</span>
                  <input
                    ref={nameRef}
                    value={name}
                    maxLength={100}
                    autoComplete="off"
                    aria-required="true"
                    aria-invalid={error ? true : undefined}
                    onChange={(e) => setName(e.target.value)}
                  />
                </label>
                <p className="muted">It is saved for your company and will be available when you add other employees.</p>
                {error && <p className="error" role="alert">{error}</p>}
              </div>
              <div className="compensation-modal-footer">
                <button type="button" className="link" onClick={close} disabled={busy}>Cancel</button>
                <button type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add Department'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
